import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/TestAuthContext';
import axios from 'axios';
import { AlertCircle, DollarSign, Calendar, TrendingUp, FileText, Download, ChevronDown, ChevronUp } from 'lucide-react';
import { formatCurrency } from '../utils/formatCurrency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface SubrogationOpportunity {
  trigger_type: string;
  category: string;
  probability_percent: number;
  projected_recovery: number;
  recovery_range_low: number;
  recovery_range_high: number;
  evidence_snippet: string;
  statute_expires?: string;
  days_until_expiration?: number;
  urgency_level: string;
  priority_score: number;
}

interface SubrogationAnalysis {
  opportunities: SubrogationOpportunity[];
  count: number;
  total_projected_recovery: number;
  highest_priority?: SubrogationOpportunity;
}

interface SubrogationPanelProps {
  reportId: string;
}

export default function SubrogationPanel({ reportId }: SubrogationPanelProps) {
  const { getToken } = useAuth();
  
  const [analysis, setAnalysis] = useState<SubrogationAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [generatingLetter, setGeneratingLetter] = useState(false);
  const [letterContent, setLetterContent] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  // Run analysis when component mounts or expands
  const runAnalysis = useCallback(async () => {
    if (!reportId) {
      console.warn('No report ID provided');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const token = await getToken();

      console.log('🔍 Running subrogation analysis for report:', reportId);

      const response = await axios.post(
        `${BACKEND_URL}/api/v1/subrogation/analyze/${reportId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('✅ Subrogation analysis result:', response.data);
      setAnalysis(response.data);
      
      // Auto-expand if opportunities found
      if (response.data.count > 0) {
        setExpanded(true);
      }
    } catch (error: any) {
      console.error('❌ Subrogation analysis error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to analyze subrogation';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [reportId, getToken]);

  useEffect(() => {
    runAnalysis();
  }, [runAnalysis]);

  const generateDemandLetter = async (opportunityIndex: number) => {
    try {
      setGeneratingLetter(true);
      const token = await getToken();

      const response = await axios.post(
        `${BACKEND_URL}/api/v1/subrogation/generate-letter/${reportId}`,
        {
          opportunity_index: opportunityIndex,
          include_evidence: true,
          include_case_law: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setLetterContent(response.data.letter_content);
    } catch (error: any) {
      console.error('Error generating demand letter:', error);
      setErrorMessage('Failed to generate demand letter: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGeneratingLetter(false);
    }
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'critical':
        return 'bg-red-100 border-red-300 text-red-900';
      case 'high':
        return 'bg-orange-100 border-orange-300 text-orange-900';
      case 'medium':
        return 'bg-yellow-100 border-yellow-300 text-yellow-900';
      case 'low':
        return 'bg-green-100 border-green-300 text-green-900';
      default:
        return 'bg-gray-100 border-gray-300 text-gray-900';
    }
  };

  if (loading && !analysis) {
    return (
      <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-6 mb-6">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-600"></div>
          <p className="text-amber-900 font-semibold">Scanning for subrogation opportunities...</p>
        </div>
      </div>
    );
  }

  if (error && !analysis) {
    return (
      <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 mb-6">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-900">Subrogation Analysis Error</p>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!analysis || analysis.count === 0) {
    return null; // Don't show anything if no opportunities
  }

  return (
    <div className="mb-6">
      {errorMessage && (
        <p className="text-red-500 text-sm mb-2">{errorMessage}</p>
      )}
      {statusMessage && (
        <p className="text-green-600 text-sm mb-2">{statusMessage}</p>
      )}
      {/* Collapsed View - Alert Banner */}
      {!expanded && (
        <button
          onClick={() => setExpanded(true)}
          className="w-full bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-xl p-4 hover:from-amber-100 hover:to-orange-100 transition-all"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <DollarSign className="w-6 h-6 text-amber-700" />
              <div className="text-left">
                <h3 className="text-lg font-bold text-amber-900">
                  🎯 {analysis.count} Subrogation Opportunit{analysis.count === 1 ? 'y' : 'ies'} Detected
                </h3>
                <p className="text-sm text-amber-800">
                  Total projected recovery: <span className="font-bold text-green-700">{formatCurrency(analysis.total_projected_recovery)}</span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="bg-amber-600 text-white px-3 py-1 rounded-full text-xs font-bold">
                HIGH PRIORITY
              </span>
              <ChevronDown className="w-5 h-5 text-amber-700" />
            </div>
          </div>
        </button>
      )}

      {/* Expanded View - Full Analysis */}
      {expanded && (
        <div className="bg-white border-2 border-amber-300 rounded-xl p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <DollarSign className="w-6 h-6 text-amber-700" />
              <h3 className="text-xl font-bold text-gray-900">Subrogation Analysis</h3>
              <span className="bg-amber-100 text-amber-800 px-2 py-1 rounded text-xs font-bold">
                {analysis.count} OPPORTUNITIES
              </span>
            </div>
            <button
              onClick={() => setExpanded(false)}
              className="text-gray-600 hover:text-gray-800 flex items-center gap-1 text-sm font-semibold"
            >
              <ChevronUp className="w-4 h-4" />
              Collapse
            </button>
          </div>

          {/* Summary Card */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border-2 border-green-200 mb-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Projected Recovery:</p>
                <p className="text-3xl font-bold text-green-700">
                  {formatCurrency(analysis.total_projected_recovery)}
                </p>
              </div>
              {analysis.highest_priority && (
                <div>
                  <p className="text-sm text-gray-600 mb-1">Top Opportunity:</p>
                  <p className="text-lg font-bold text-gray-900">{analysis.highest_priority.trigger_type}</p>
                  <p className="text-sm text-gray-600">
                    {formatCurrency(analysis.highest_priority.projected_recovery)} 
                    ({analysis.highest_priority.probability_percent}% probability)
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Opportunities List */}
          <div className="space-y-4">
            {analysis.opportunities.map((opp, index) => (
              <div 
                key={index}
                className={`rounded-lg p-4 border-2 ${getUrgencyColor(opp.urgency_level)}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-lg font-bold">{opp.trigger_type}</h4>
                      <span className="text-xs bg-white/50 px-2 py-0.5 rounded font-semibold">
                        {opp.category}
                      </span>
                    </div>
                    <p className="text-sm opacity-80">{opp.evidence_snippet}</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-4 gap-4 mb-3">
                  <div>
                    <p className="text-xs font-semibold mb-1">Recovery Amount</p>
                    <p className="text-xl font-bold text-green-700">
                      {formatCurrency(opp.projected_recovery)}
                    </p>
                    <p className="text-xs opacity-70">
                      Range: {formatCurrency(opp.recovery_range_low)} - {formatCurrency(opp.recovery_range_high)}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-semibold mb-1">Success Probability</p>
                    <p className="text-xl font-bold">{opp.probability_percent}%</p>
                    <div className="w-full bg-white/50 rounded-full h-2 mt-1">
                      <div 
                        className="bg-green-600 h-2 rounded-full"
                        style={{ width: `${opp.probability_percent}%` }}
                      ></div>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold mb-1">Priority Score</p>
                    <p className="text-xl font-bold">{opp.priority_score.toFixed(1)}</p>
                    <p className="text-xs opacity-70">of 100</p>
                  </div>

                  {opp.days_until_expiration !== undefined && (
                    <div>
                      <p className="text-xs font-semibold mb-1 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Statute Expires
                      </p>
                      <p className="text-xl font-bold">
                        {opp.days_until_expiration} days
                      </p>
                      <p className="text-xs opacity-70 uppercase font-bold">
                        {opp.urgency_level} urgency
                      </p>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => generateDemandLetter(index)}
                  disabled={generatingLetter}
                  className="w-full bg-amber-700 hover:bg-amber-800 text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  {generatingLetter ? 'Generating...' : 'Generate Demand Letter'}
                </button>
              </div>
            ))}
          </div>

          {/* Demand Letter Modal */}
          {letterContent && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-xl p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold">Demand Letter</h3>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(letterContent);
                        setStatusMessage('Letter copied to clipboard!');
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Copy
                    </button>
                    <button
                      onClick={() => setLetterContent(null)}
                      className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                    >
                      Close
                    </button>
                  </div>
                </div>
                <pre className="whitespace-pre-wrap font-mono text-sm bg-gray-50 p-4 rounded border">
                  {letterContent}
                </pre>
              </div>
            </div>
          )}

          {/* Info Footer */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900">
              <strong>Court-Defensible Analysis:</strong> Opportunities identified using 42 verified trigger patterns 
              with state-specific recovery rates, comparative fault analysis, and statute of limitations tracking.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
